"use client";

import { AnimatedSection } from "@/components/ui/animated-section";
import { useTranslations, useLocale } from 'next-intl';

interface PrivacyClientProps {
  locale: string;
}

export function PrivacyClient({ locale }: PrivacyClientProps) {
  const currentLocale = useLocale();
  const t = useTranslations('privacy');

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-20 pb-8 md:pt-24 md:pb-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up" delay={0}>
            <div className="text-left">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                {t('title')}
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-4 md:mb-6 max-w-3xl">
                {t('description')}
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 md:py-12 pb-16 md:pb-20">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <article className="prose prose-lg max-w-none">
              <AnimatedSection animation="fade-up" delay={0}>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('lastUpdated')}
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={50}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.introduction.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.introduction.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.dataCollection.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.dataCollection.content')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.dataCollection.items.name')}</li>
                    <li>{t('sections.dataCollection.items.email')}</li>
                    <li>{t('sections.dataCollection.items.phone')}</li>
                    <li>{t('sections.dataCollection.items.orderDetails')}</li>
                    <li>{t('sections.dataCollection.items.paymentInfo')}</li>
                  </ul>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={150}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.dataUsage.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.dataUsage.content')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.dataUsage.items.processing')}</li>
                    <li>{t('sections.dataUsage.items.communication')}</li>
                    <li>{t('sections.dataUsage.items.improvement')}</li>
                    <li>{t('sections.dataUsage.items.legal')}</li>
                  </ul>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={200}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.cookies.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.cookies.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.cookies.manage')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={250}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.dataSecurity.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.dataSecurity.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={300}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.userRights.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.userRights.content')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.userRights.items.access')}</li>
                    <li>{t('sections.userRights.items.correction')}</li>
                    <li>{t('sections.userRights.items.deletion')}</li>
                    <li>{t('sections.userRights.items.objection')}</li>
                    <li>{t('sections.userRights.items.portability')}</li>
                  </ul>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={350}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.dataSharing.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.dataSharing.content')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.dataSharing.items.payment')}</li>
                    <li>{t('sections.dataSharing.items.hosting')}</li>
                    <li>{t('sections.dataSharing.items.analytics')}</li>
                    <li>{t('sections.dataSharing.items.legal')}</li>
                  </ul>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={400}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.dataRetention.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.dataRetention.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={450}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.childrenPrivacy.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.childrenPrivacy.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={500}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.contact.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.contact.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    <strong>{t('sections.contact.email')}</strong> littlecakesnl@gmail.com
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={550}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.changes.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t('sections.changes.content')}
                  </p>
                </section>
              </AnimatedSection>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
